import {
    ILayoutsProfileSection,
    ISiteConfigLayouts,
    ProfileLayoutSectionEnum,
} from "@finbackoffice/fe-core";
import { useCallback, useMemo } from "react";
import { useSiteConfig } from "./useSiteConfig";
import { useRuntimeConfig } from "./useRuntimeConfig";

export const useLayoutProfileConfig = (
    name?: ProfileLayoutSectionEnum,
    nestedSectionName?: ProfileLayoutSectionEnum,
    configKey?: "profile" | "profileTablet",
) => {
    const siteLayoutsConfig = useSiteConfig<ISiteConfigLayouts>("layouts");
    const { crm, chatService, verification } = useRuntimeConfig("COMMON_SITE_CONFIGS");

    const profileConfig = useMemo(() => {
        return configKey === "profileTablet"
            ? siteLayoutsConfig.profileTablet
            : (siteLayoutsConfig.profile.config as ILayoutsProfileSection[]);
    }, [siteLayoutsConfig, configKey]);

    const sectionConfig: ILayoutsProfileSection | null = useMemo(() => {
        return profileConfig.find((section) => section.name === name && section.enable) || null;
    }, [name, profileConfig]);

    const nestedSectionConfig: ILayoutsProfileSection | null = useMemo(() => {
        return (
            sectionConfig?.config?.find(
                (section) => section.name === nestedSectionName && section.enable,
            ) || null
        );
    }, [nestedSectionName, sectionConfig?.config]);

    const allEnabledConfigs = useMemo(
        () => profileConfig.filter((config) => config.enable),
        [profileConfig],
    );

    const getFilteredLayoutProfileConfigs = useCallback(
        (configs?: ILayoutsProfileSection[]) => {
            const enabledConfigs =
                configs && configs.length > 0
                    ? configs.filter((config) => config.enable)
                    : allEnabledConfigs;
            let result = structuredClone(enabledConfigs ?? null);

            if (result) {
                const gamificationEnabled = result?.find(
                    (config) => config.name === ProfileLayoutSectionEnum.Gamification,
                )?.enable;

                if (gamificationEnabled && (!crm.enable || !crm.config.gamification)) {
                    result = result.filter(
                        (config) => config.name !== ProfileLayoutSectionEnum.Gamification,
                    );
                }

                if (!chatService.enable || chatService.showAfterSignIn) {
                    result = result.filter(
                        (config) => config.name !== ProfileLayoutSectionEnum.ChatService,
                    );
                }

                const verificationEnabled = result?.find(
                    (config) => config.name === ProfileLayoutSectionEnum.Verification,
                )?.enable;

                if (verificationEnabled && !verification.enable) {
                    result = result.filter(
                        (config) => config.name !== ProfileLayoutSectionEnum.Verification,
                    );
                }
            }

            return result;
        },
        [crm, chatService, verification, allEnabledConfigs],
    );

    return {
        allConfigs: allEnabledConfigs,
        getFilteredLayoutProfileConfigs,
        config: nestedSectionName ? nestedSectionConfig : sectionConfig,
    };
};
